import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { LayoutPrivate, LayoutPublic } from '@/containers/Layout';
import { useGlobalContext } from '@/context/global.context';
import Accesibilidad from '@/pages/Accesibilidad';
import Access from '@/pages/Access';
import Administrar from '@/pages/Administrar';
import AvisoLegal from '@/pages/AvisoLegal';
import Home from '@/pages/Home';
import Landing from '@/pages/Landing';
import LibroSocios from '@/pages/LibroSocios';
import Mensaje from '@/pages/Mensajes';
import PoliticaCookies from '@/pages/PoliticaCookies';
import Solicitudes from '@/pages/Solicitudes';
import Usuario from '@/pages/Administrar/Usuario';
import { useErrorMessageStore } from '@/store/error-message.store';

import { ROUTES } from '@/constants/routes.constants';
import { getUrlTramite } from '@/helpers/navigation';
import { notificationsConfig } from '@/helpers/notifications';

import RoutesTramite from './RoutesTramite';

const RedirectHome = () => {
	const { user } = useGlobalContext();
	return <Navigate to={user?.token ? ROUTES.HOME : ROUTES.PORTADA} />;
};

function PrivateRoute() {
	const { user } = useGlobalContext();
	if (user?.token) {
		return (
			<LayoutPrivate>
				<Outlet />
			</LayoutPrivate>
		);
	}
	return <Navigate to={ROUTES.PORTADA} />;
}

export default function AppRoutes() {
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { user, getTramitesContratados } = useGlobalContext();
	const errorMessage = useErrorMessageStore((state) => state.errorMessage);
	const clearMessage = useErrorMessageStore((state) => state.clearMessage);

	const tramitesList = useMemo(
		() =>
			getTramitesContratados().map((data) => ({
				url: getUrlTramite(data),
				id: data.tipoTramite.id,
			})) ?? [],
		[user],
	);

	useEffect(() => {
		if (errorMessage) {
			setTimeout(() => {
				closeNotification();
				openNotification(notificationTemplate.errorMessage(errorMessage, clearMessage));
			}, 0);
		}
	}, [errorMessage]);

	return (
		<Routes>
			<Route path={ROUTES.KEYCLOAK_REDIRECT} element={<Access />} />
			<Route path={ROUTES.PORTADA} element={<Landing />} />
			<Route
				path={ROUTES.AVISO_LEGAL}
				element={
					<LayoutPublic isButtonBack>
						<AvisoLegal />
					</LayoutPublic>
				}
			/>

			<Route
				path={ROUTES.ACCESIBILIDAD}
				element={
					<LayoutPublic isButtonBack>
						<Accesibilidad />
					</LayoutPublic>
				}
			/>
			<Route
				path={ROUTES.POLITICA_COOKIES}
				element={
					<LayoutPublic isButtonBack>
						<PoliticaCookies />
					</LayoutPublic>
				}
			/>
			<Route element={<PrivateRoute />}>
				<Route path={ROUTES.HOME} element={<Home />} />
				<Route path={ROUTES.LIBRO_SOCIOS} element={<LibroSocios />}/>
				<Route path={ROUTES.SOLICITUDES} element={<Solicitudes />} />
				<Route path={ROUTES.ADMINISTRAR} element={<Administrar />} />
				<Route path={ROUTES.USER_NEW} element={<Usuario />} />
				<Route path={ROUTES.MENSAJES} element={<Mensaje />} />
				<Route path={`${ROUTES.USER}/:id`} element={<Usuario />} />
				{tramitesList.map(({ url, id }) => (
					<Route key={id} path={`${url}/*`} element={<RoutesTramite tipoTramiteId={id} />} />
				))}
			</Route>
			<Route path="*" element={<RedirectHome />} />
		</Routes>
	);
}
